import React, { useState } from "react";
import { PlatformDashboardCards } from "./PlatformDashboardCards";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import {
  formatNumber,
  toIntPercent,
  formatDuration,
} from "../../utils/constant";

import {
  Box,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  Stack,
} from "@mui/material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { TopCustomersTable } from "../table/TopCustomersTable";
import { Skeleton } from "@mui/material";

export const PlatformDashboardContent = ({
  summaryLoading,
  growthLoading,
  apiUsageLoading,
  creditLoading,
  healthLoading,
  categoryLoading,
  tableLoading,
  totalCustomers,
  activeCustomers,
  totalApiCalls,
  totalCreditConsumed,
  dashboardApiUsageData,
  dashboardCustomerGrothData,
  dashboardCreditConsumptionData,
  dashboardPlatformHealthdata,
  dashboardApiCactegoryUsagedata,
  customers,
  totalCustomersCount,
  page,
  rowsPerPage,
  onPageChange,
}) => {
  const [showAllApis, setShowAllApis] = useState(false);
  const MAX_VISIBLE = 6;
  const apiDistribution = dashboardApiCactegoryUsagedata?.distribution || [];

  const hasTopApiData =
    Array.isArray(apiDistribution) &&
    apiDistribution.length > 0 &&
    apiDistribution.some((item) => Number(item.percentage) > 0);

  const visibleApis = showAllApis
    ? apiDistribution
    : apiDistribution.slice(0, MAX_VISIBLE);

  const generateColors = (count) => {
    return Array.from({ length: count }, (_, i) => {
      const hue = Math.floor((360 / count) * i);
      return `hsl(${hue}, 70%, 50%)`;
    });
  };

  const colors = generateColors(
    dashboardApiCactegoryUsagedata?.distribution?.length || 0
  );

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { api_category, percentage } = payload[0].payload;

      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            boxShadow: 3,
            borderRadius: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography fontSize="13px" fontWeight={600}>
            {api_category}
          </Typography>
          <Typography fontSize="12px" color="text.secondary">
            {toIntPercent(percentage)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const formatCreditConsumptionData = (apiData = []) => {
    return apiData.map((item) => ({
      month: new Date(item.month + "-01").toLocaleString("en-US", {
        month: "short",
      }),
      credits: item.totalCreditConsumed ?? 0,
    }));
  };

  const SectionTitle = ({ icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      {icon}
      <Typography variant="h4" fontWeight="bold">
        {title}
      </Typography>
    </Box>
  );

  const creditConsumptionData = formatCreditConsumptionData(
    dashboardCreditConsumptionData?.data
  );

  const ChartSkeleton = ({ height = 300 }) => (
    <Skeleton variant="rounded" height={height} animation="wave" />
  );
  
  const PieSkeleton = () => (
    <Stack alignItems="center" justifyContent="center" height={220}>
      <Skeleton variant="circular" width={160} height={160} />
    </Stack>
  );
  
  const StatSkeleton = () => (
    <Stack spacing={1.5} alignItems="center">
      <Skeleton width={120} height={40} />
      <Skeleton width={180} height={20} />
    </Stack>
  );

  const PieLegendSkeleton = ({ items = 4 }) => {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
          mt: 2,
        }}
      >
        {Array.from({ length: items }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Left: color dot + name */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={10} height={10} />
              <Skeleton width={70} height={14} />
            </Stack>
  
            {/* Right: percentage */}
            <Skeleton width={30} height={14} />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* Dashboard Cards */}
      <Box sx={{ mb: 4 }}>
        <PlatformDashboardCards
          loading={summaryLoading}
          activeCustomers={activeCustomers}
          totalApiCalls={totalApiCalls}
          totalCreditConsumed={totalCreditConsumed}
        />
      </Box>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Customer Growth */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 2, pb: 2, '&:last-child': { pb: 2 } }}>
              <SectionTitle
                title="Customer Growth"
                icon={
                  <ApartmentOutlinedIcon
                    sx={{ fontSize: 22, color: "#4F46E5" }}
                  />
                }
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontWeight: 400,
                  fontSize: "1.4rem",
                }}
              >
                New vs churned customers over time
              </Typography>
              <Box sx={{ p: 2, borderRadius: 2 }}>
                {growthLoading ? (
                  <ChartSkeleton />
                ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={dashboardCustomerGrothData?.bars}
                    barGap={8}
                    barSize={36}
                    margin={{ left: -20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={true}
                      horizontal={true}
                      stroke="#E9EDF5"
                    />
                    <XAxis
                      dataKey="label"
                      tickFormatter={(label) => {
                        const date = label.split(" → ")[1];
                        return new Date(date).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        });
                      }}
                      tick={{ fill: "#667085", fontSize: 12 }}
                      axisLine={true}
                      tickLine={true}
                    />
                    <YAxis
                      tick={{ fill: "#667085", fontSize: 12 }}
                      axisLine={true}
                      tickLine={true}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        fontSize: 13,
                      }}
                      labelFormatter={(label) => {
                        const [start, end] = label.split(" → ");

                        const formatDate = (date) =>
                          new Date(date).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                          });

                        return `${formatDate(start)} → ${formatDate(end)}`;
                      }}
                      formatter={(value, name) => {
                        const labelMap = {
                          newCustomers: "New Customers",
                          churnedCustomers: "Churned Customers",
                        };
                        return [
                          `${value.toLocaleString()}`,
                          labelMap[name] || name,
                        ];
                      }}
                    />
                    <Bar
                      dataKey="newCustomers"
                      fill="#4F6BED"
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar
                      dataKey="churnedCustomers"
                      fill="#F04438"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* API Usage Trend */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 2, pb: 2, '&:last-child': { pb: 2 } }}>
              <SectionTitle
                title="API Usage Trend"
                icon={
                  <ShowChartOutlinedIcon
                    sx={{ fontSize: 22, color: "#2563EB" }}
                  />
                }
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontWeight: 400,
                  fontSize: "1.4rem",
                }}
              >
                Platform-wide API calls over time
              </Typography>
              <Box sx={{ p: 2, borderRadius: 2 }}>
              {apiUsageLoading ? (
                  <ChartSkeleton />
                ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart
                    data={dashboardApiUsageData?.bars}
                    margin={{ top: 10, right: 20, left: -20, bottom: 0 }}
                  >
                    {/* Gradient fill */}
                    <defs>
                      <linearGradient
                        id="apiUsageGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#4f7cff"
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="100%"
                          stopColor="#4f7cff"
                          stopOpacity={0.05}
                        />
                      </linearGradient>
                    </defs>

                    {/* Grid */}
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={true}
                      horizontal={true}
                      stroke="#e9eef5"
                    />

                    {/* X Axis */}
                    <XAxis
                      dataKey="label"
                      tickFormatter={(label) => {
                        const date = label.split(" → ")[1];
                        return new Date(date).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        });
                      }}
                      axisLine={true}
                      tickLine={true}
                      tick={{ fill: "#6b7280", fontSize: 13 }}
                    />

                    {/* Y Axis */}
                    <YAxis
                      axisLine={true}
                      tickLine={true}
                      tick={{ fill: "#6b7280", fontSize: 13 }}
                      tickFormatter={(value) => formatNumber(value)}
                    />

                    {/* Tooltip */}
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        fontSize: 13,
                      }}
                      labelFormatter={(label) => {
                        const [start, end] = label.split(" → ");

                        const formatDate = (date) =>
                          new Date(date).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                          });

                        return `${formatDate(start)} → ${formatDate(end)}`;
                      }}
                      formatter={(value) => [formatNumber(value), "API Calls"]}
                    />
                    {/* Area */}
                    <Area
                      type="monotone"
                      dataKey="successfulHits"
                      stroke="#4f7cff"
                      strokeWidth={2}
                      fill="url(#apiUsageGradient)"
                      dot={false}
                      activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* NEW: Top APIs, Platform Health, Credit Consumption */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={3} alignItems="stretch">
          {/* Top APIs by Usage */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 3,
                height: "100%",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Top APIs by Usage
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    fontWeight: 400,
                    fontSize: "1.4rem",
                  }}
                >
                  Distribution across all customers
                </Typography>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ width: "100%", height: 220 }}>
                  {categoryLoading ? (
                      <PieSkeleton />
                    ) : hasTopApiData ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={apiDistribution}
                            innerRadius={60}
                            outerRadius={90}
                            dataKey="percentage"
                          >
                            {apiDistribution.map((_, index) => (
                              <Cell key={index} fill={colors[index]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Stack
                        sx={{ width: "100%", height: "100%" }}
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        spacing={1}
                      >
                        <Typography
                          fontSize={14}
                          fontWeight={600}
                          color="#64748b"
                        >
                          No API usage data
                        </Typography>
                        <Typography fontSize={12} color="#94a3b8">
                          Data will appear once APIs are used
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Box>

                {/* ===== LEGEND (ONLY IF DATA EXISTS) ===== */}
                {categoryLoading ? (
        <PieLegendSkeleton />
      ) : hasTopApiData ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.5,
                        mt: 2,
                      }}
                    >
                      {visibleApis.map((item, index) => (
                        <Box
                          key={item.api_category}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: colors[index],
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 85,
                                overflow: "hidden",
                                fontSize: "15px",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.api_category}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: 700,
                              color: "#020817",
                            }}
                          >
                            {toIntPercent(item.percentage)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {apiDistribution.length > MAX_VISIBLE && (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: 600,
                            color: "#2563EB",
                          }}
                          onClick={() => setShowAllApis((prev) => !prev)}
                        >
                          {showAllApis ? "See less" : "See more..."}
                        </Typography>
                      </Box>
                    )}
                  </>
      ) : null}
              </CardContent>
            </Card>
          </Grid>

          {/* Platform Health */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 3,
                height: "100%",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <SectionTitle
                  title="Platform Health"
                  icon={
                    <CheckCircleOutlineOutlinedIcon
                      sx={{ fontSize: 22, color: "#16A34A" }}
                    />
                  }
                />
                <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 400,
                        fontSize: "1.4rem",
                      }}
                    >
                      Overall Success Rate
                    </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                    flexGrow: 1,
                  }}
                >
                  {healthLoading ? (
                    <StatSkeleton />
                  ) : (
                    <>
                    <Typography
                      variant="h1"
                      fontWeight={700}
                      color="success.main"
                    >
                      {`${toIntPercent(
                        dashboardPlatformHealthdata?.overallSuccessRate ?? 0
                      )}`}
                    </Typography>
                    
                  </>
                )}
                </Box>

                <Box sx={{ my: 1 }} />

                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccessTimeOutlinedIcon
                        sx={{ fontSize: 18, color: "#2563EB" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "15px", fontWeight: 500 }}
                      >
                        Avg Response Time
                      </Typography>
                    </Box>
                    {healthLoading ? (
                      <Skeleton height={24} />
                    ) : (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#020817",
                      }}
                    >
                      {formatDuration(
                        dashboardPlatformHealthdata?.avgResponseTimeMs
                      )}
                    </Typography>
                    )}
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PeopleOutlineOutlinedIcon
                        sx={{ fontSize: 18, color: "#4F46E5" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "15px", fontWeight: 500 }}
                      >
                        Total Customers
                      </Typography>
                    </Box>
                    {healthLoading ? (
                      <Skeleton height={24} />
                    ) : (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#020817",
                      }}
                    >
                      {totalCustomers}
                    </Typography>
                    )}
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ShowChartOutlinedIcon
                        sx={{ fontSize: 18, color: "#06B6D4" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "15px", fontWeight: 500 }}
                      >
                        APIs Available
                      </Typography>
                    </Box>
                    {healthLoading ? (
                      <Skeleton height={24} />
                    ) : (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#020817",
                      }}
                    >
                      {dashboardPlatformHealthdata?.availableApis}
                    </Typography>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Credit Consumption */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 3,
                height: "100%",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  pb: 2,
                  '&:last-child': { pb: 2 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Credit Consumption
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    fontWeight: 400,
                    fontSize: "1.4rem",
                  }}
                >
                  Monthly trend
                </Typography>

                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minHeight: 240 }}>
                {creditLoading ? (
                    <ChartSkeleton height={240} />
                  ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={creditConsumptionData} margin={{ left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatNumber(value)} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          fontSize: 13,
                        }}
                        formatter={(value) => [formatNumber(value), "Credits"]}
                      />
                      <Line
                        dataKey="credits"
                        stroke="#2563EB"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Customers Table */}
      <Box sx={{ mt: 3 }}>
        <TopCustomersTable
          tableLoading={tableLoading}
          data={customers}
          totalCount={totalCustomersCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
        />
      </Box>
      <Box sx={{ mt: 1 }}> </Box>
    </Box>
  );
};
